import { z } from "zod";
import { ACTIVITY_IDS, GENDER, GOAL, SKILL_LEVEL } from "@/db/schema/profile";
import {
  USERNAME_ERROR_MESSAGES,
  USERNAME_MAX_LENGTH,
  USERNAME_MIN_LENGTH,
  validateUsername,
} from "@/lib/handles";
import { getAge } from "@/lib/utils";
import { mediaAssetSlimSchema } from "@/validations/media";

export const MIN_PHOTOS = 1;
export const MAX_PHOTOS = 6;
export const MIN_BIO_LENGTH = 30;
export const MAX_BIO_LENGTH = 500;
export const MIN_GOALS = 1;
export const MAX_GOALS = GOAL.length;
export const MIN_ACTIVITIES = 1;
export const MAX_ACTIVITIES = ACTIVITY_IDS.length;

// Individual field schemas
export const imageSchema = mediaAssetSlimSchema;

export const locationSchema = z.object({
  displayName: z.string().min(1, "Location name is required"),
  latitude: z.number().min(-90).max(90, "Invalid latitude"),
  longitude: z.number().min(-180).max(180, "Invalid longitude"),
});

export const activitySkillSchema = z.object({
  // Plain string (not a static enum) so runtime-added activities validate.
  // step 3 cross-checks each id against the user's saved activities.
  activityId: z.string().min(1),
  skillLevel: z.number().min(1).max(7),
});

export const usernameSchema = z
  .string()
  .trim()
  .min(USERNAME_MIN_LENGTH, USERNAME_ERROR_MESSAGES.too_short)
  .max(USERNAME_MAX_LENGTH, USERNAME_ERROR_MESSAGES.too_long)
  .refine((value) => value === value.toLowerCase(), {
    message: USERNAME_ERROR_MESSAGES.invalid_characters,
  })
  .superRefine((value, ctx) => {
    const result = validateUsername(value, {
      minLength: USERNAME_MIN_LENGTH,
      maxLength: USERNAME_MAX_LENGTH,
    });

    if (!result.valid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: USERNAME_ERROR_MESSAGES[result.error],
      });
    }
  });

// Individual enum schemas
export const skillLevelSchema = z.enum(SKILL_LEVEL);
// A blank/missing gender is the only way this can fail, and the default zod
// enum message leaks the raw option list ("Invalid option: expected one of
// "male"|"female"|"other""), so the copy is set here for every caller.
export const genderSchema = z.enum(GENDER, {
  error: "Please select your gender",
});
export const goalSchema = z.enum(GOAL);
export const activityIdSchema = z.string().min(1);

// Define refined schemas separately to avoid TypeScript "excessively deep" error
const fullNameSchema = z
  .string()
  .trim()
  .min(1, "Please enter your full name")
  .max(100, "Full name must be less than 100 characters");

// `abort` on the empty check so a blank value reports "pick a date" only —
// without it zod also runs the age refinement and stacks a second, confusing
// "You must be at least 13 years old" onto an untouched field.
const dateOfBirthSchema = z
  .string()
  .min(1, { message: "Please pick your date of birth", abort: true })
  .refine((date) => {
    const age = getAge(date);
    return age !== null && age >= 13;
  }, "You must be at least 13 years old");

const imagesArraySchema = z
  .array(imageSchema)
  .min(MIN_PHOTOS, `Please upload at least ${MIN_PHOTOS} photo`)
  .max(MAX_PHOTOS, `You can upload up to ${MAX_PHOTOS} photos`);

const bioSchema = z
  .string()
  .trim()
  .min(MIN_BIO_LENGTH, `Bio must be at least ${MIN_BIO_LENGTH} characters`)
  .max(MAX_BIO_LENGTH, `Bio must be less than ${MAX_BIO_LENGTH} characters`);

const goalsArraySchema = z
  .array(goalSchema)
  .min(MIN_GOALS, "Please select at least one goal")
  .max(MAX_GOALS, "Please select no more than four goals");

const locationDisplayNameSchema = z
  .string()
  .min(1, "Location name is required");
const locationLatitudeSchema = z.number().min(-90).max(90, "Invalid latitude");
const locationLongitudeSchema = z
  .number()
  .min(-180)
  .max(180, "Invalid longitude");

// Base profile insert schema - defined with pure Zod to avoid TS2589 error
// This matches the database schema structure with all fields nullable/optional
export const profileInsertSchema = z
  .object({
    userId: z.string().optional(),
    fullName: fullNameSchema.nullable().optional(),
    gender: genderSchema.nullable().optional(),
    dateOfBirth: dateOfBirthSchema.nullable().optional(),
    images: imagesArraySchema.nullable().optional(),
    bio: bioSchema.nullable().optional(),
    goals: goalsArraySchema.nullable().optional(),
    locationDisplayName: locationDisplayNameSchema.nullable().optional(),
    locationLatitude: locationLatitudeSchema.nullable().optional(),
    locationLongitude: locationLongitudeSchema.nullable().optional(),
    geoHash: z.string().nullable().optional(),
    isComplete: z.boolean().optional(),
    onboardingStep: z.number().int().optional(),
    onboardingCompletedAt: z.date().nullable().optional(),
    showInDiscover: z.boolean().optional(),
    showInSearch: z.boolean().optional(),
    autoPostEventAttendance: z.boolean().optional(),
    autoPostActivityResults: z.boolean().optional(),
    allowStoryReshare: z.boolean().optional(),
    showLastSeen: z.boolean().optional(),
    sendReadReceipts: z.boolean().optional(),
    createdAt: z.date().optional(),
    updatedAt: z.date().optional(),
  })
  .strict();

// Creation schema (omit auto-generated fields)
export const profileCreateSchema = profileInsertSchema.omit({
  userId: true,
  geoHash: true, // Auto-calculated from coordinates
  createdAt: true,
  updatedAt: true,
  isComplete: true,
});

/**
 * Create a dynamic activitySkills schema that validates against specific activities
 */
export function createActivitySkillsSchema(selectedActivities: string[]) {
  return z
    .array(activitySkillSchema)
    .min(1, "Please set skill levels for your selected activities")
    .refine(
      (skills) => {
        // Check that we have exactly one skill entry for each selected activity
        const skillActivityIds = skills.map((skill) => skill.activityId);
        const uniqueSkillIds = new Set(skillActivityIds);

        // No duplicate skill entries
        if (skillActivityIds.length !== uniqueSkillIds.size) {
          return false;
        }

        // Must have skill for each selected activity
        if (skills.length !== selectedActivities.length) {
          return false;
        }

        // Each skill must be for a selected activity
        return skills.every((skill) =>
          selectedActivities.includes(skill.activityId),
        );
      },
      {
        message:
          "Please set skill levels for all your selected activities (one skill per activity)",
      },
    );
}

/**
 * Create a dynamic step3 schema that validates activitySkills against saved activities
 */
export function createStep3Schema(selectedActivities: string[]) {
  return z.object({
    activitySkills: createActivitySkillsSchema(selectedActivities),
  });
}

// Type exports
export type ProfileCreateData = z.infer<typeof profileCreateSchema>;

/**
 * Schema for a complete profile where all fields are required and non-null
 */
export const completeProfileSchema = profileInsertSchema.extend({
  userId: z.string(), // Required for a complete profile
  isComplete: z.literal(true),
  fullName: z.string().min(1),
  gender: genderSchema,
  dateOfBirth: z.string().min(1),
  images: z.array(imageSchema).min(MIN_PHOTOS),
  bio: z.string().min(MIN_BIO_LENGTH),
  goals: z.array(goalSchema).min(MIN_GOALS),
  locationDisplayName: z.string().min(1),
  locationLatitude: z.number(),
  locationLongitude: z.number(),
});

export type CompleteProfile = z.infer<typeof completeProfileSchema>;

/**
 * View-time parse of a raw `profile` DB row. Tolerates columns the
 * validation layer doesn't know about yet, so adding a DB column can never
 * make every public profile "unavailable" (the insert schema stays strict).
 */
export const completeProfileRowSchema = completeProfileSchema.catchall(
  z.unknown(),
);
